import { useEffect, useState } from "react";
import { useLessonVoiceMoment } from "@/hooks/useLessonVoiceMoment";
import { instructorPortraitMap } from "@/components/launch/InstructorVoiceCaption";

/**
 * Floating mentor capsule for a currently-playing lesson voice moment.
 * Separate from `InstructorVoiceCaption` (modules) so the two systems
 * can render at the same time without state collisions.
 *
 * Anchored bottom-center on mobile, lower-right on desktop, so it does
 * not overlap the module caption (top-left) or lesson video CC (centered
 * inside the video frame).
 *
 * @deprecated Import the default export from
 * `@/components/lesson/LessonVoiceMomentCaption` instead. This filename
 * is preserved as the implementation home for backward compatibility
 * during the terminology migration.
 */
export default function LessonInterjectionCaption() {
  const { speakingMomentId, getLessonMoment } = useLessonVoiceMoment();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(Boolean(speakingMomentId));
  }, [speakingMomentId]);

  if (!speakingMomentId) return null;
  const moment = getLessonMoment(speakingMomentId);
  if (!moment) return null;

  const meta = instructorPortraitMap[moment.instructor];
  const portrait = meta?.portrait;
  const accent = meta?.accentClass ?? "from-primary/50 to-accent/40";
  const name = meta?.name ?? "";

  return (
    <div
      aria-live="polite"
      role="status"
      className={`pointer-events-none fixed z-40 transition-all duration-300 ease-out
        left-1/2 -translate-x-1/2 bottom-[max(env(safe-area-inset-bottom),5.5rem)] w-[calc(100%-2rem)] max-w-[420px]
        md:left-auto md:right-[clamp(20px,5vw,72px)] md:translate-x-0 md:bottom-[clamp(96px,14vh,160px)] md:w-[min(360px,32vw)] md:max-w-[360px]
        ${visible ? "opacity-100 translate-y-0" : "translate-y-2 opacity-0"}`}
    >
      <div className="pointer-events-auto relative rounded-3xl border border-primary/25 bg-[hsl(var(--background)/0.88)] px-4 pt-10 pb-4 shadow-[0_18px_60px_-18px_hsl(var(--primary)/0.6)] backdrop-blur-xl md:px-5 md:pt-12 md:pb-5">
        <div className="absolute -left-3 -top-5 md:-left-4 md:-top-6">
          <div className="relative">
            <div
              className={`absolute -inset-1 rounded-full bg-gradient-to-br ${accent} opacity-80 blur-md`}
              aria-hidden
            />
            {portrait ? (
              <img
                src={portrait}
                alt={name}
                width={56}
                height={56}
                loading="eager"
                decoding="sync"
                fetchPriority="high"
                className="relative h-12 w-12 rounded-full border border-primary/40 object-cover shadow-[0_4px_18px_-4px_hsl(var(--primary)/0.6)] md:h-16 md:w-16"
              />
            ) : (
              <div
                className={`relative h-12 w-12 rounded-full border border-primary/40 bg-gradient-to-br ${accent} md:h-16 md:w-16`}
              />
            )}
          </div>
        </div>
        <div className="min-w-0">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-[0.2em] text-primary/90">
            {name}
          </div>
          <div className="text-sm leading-snug text-foreground/95 md:text-[14.5px]">
            {moment.transcript}
          </div>
        </div>
      </div>
    </div>
  );
}